import { AbstractControl, ValidatorFn } from '@angular/forms';
import { hasProperty, isEmptyString, isNil, isRecord } from '@fmnts/core';
import {
  isAfter,
  isBefore,
  isDate,
  isInvalidDate,
  parseISO,
} from '@fmnts/core/chronos';
import type { MomentInput } from 'moment';

interface DateInvalidError {
  error: 'invalid';
}

interface DateRangeMinError {
  error: 'min';
  /** Minimum that was violated */
  min: MomentInput;
}

interface DateRangeMaxError {
  error: 'max';
  /** Maximum that was violated */
  max: MomentInput;
}

/**
 * Erros from the `dateRangeValidator`.
 */
export type DateRangeValidationError =
  DateInvalidError | DateRangeMinError | DateRangeMaxError;

/**
 * Type guard that checks if a validation errors object has
 * date range errors.
 *
 * @param e Validation errors object
 *
 * @returns
 * `true` when the given errors `e` has date range errors.
 */
export const hasDateRangeError = hasProperty(
  'dateRange',
  (u): u is DateRangeValidationError => isRecord(u),
);

/**
 * @returns
 * Validator function that checks if control's
 * value is a valid date and between `min` and `max`
 */
export function dateRangeValidator(
  min?: MomentInput,
  max?: MomentInput,
): ValidatorFn {
  const isBeforeMin = min
    ? isBefore(min, { granularity: 'day', exclusive: true })
    : null;
  const isAfterMax = max
    ? isAfter(max, { granularity: 'day', exclusive: true })
    : null;

  return (
    control: AbstractControl,
  ): Record<'dateRange', DateRangeValidationError> | null => {
    // Don't handle empty inputs
    if (isNil(control.value) || isEmptyString(control.value)) {
      return null;
    }

    const date = isDate(control.value)
      ? control.value
      : parseISO(control.value);

    // Check for invalid date
    if (isInvalidDate(date)) {
      return { dateRange: { error: 'invalid' } };
    }

    if (isBeforeMin?.(date)) {
      return { dateRange: { error: 'min', min } };
    }

    if (isAfterMax?.(date)) {
      return { dateRange: { error: 'max', max } };
    }

    return null;
  };
}
